import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MAIN_MARKETS_TYPE } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { arrayMove } from 'utils/generic/array-move';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import {
  filterFavGroups,
  getFavGroupsCount
} from 'utils/sportsbook/fav-market-group';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  Game,
  Market,
  MarketGroup,
  MarketGroupItem
} from 'interfaces/sportsbook-data-levels';
import { LVision } from './LVision';
import { MarketsGroupItems } from './partials/MarketsGroupItems';
import { MarketsGroupItemsNoLazy } from './partials/MarketsGroupItemsNoLazy';
import { RocketBet } from './RocketBet';
import { MarketsListSkeleton } from './Skeleton';
import { MarketColumn, MarketsWrapper } from './style';
import { BetsMenu } from 'components/BetsMenu';
import { EmptyFavoritesSkeleton } from 'components/EmptyResultBoxes/EmptyFavorites/EmptyFavoritesSkeleton';
import { FavoriteButtonMarket } from 'components/FavoriteButton';
import { TextInfo } from 'components/GameDetails/TextInfo';
import { EmptyMarketsView } from 'components/MarketsList/EmptyMarketsView';
import 'components/MarketsList/index.less';
import { MatchCardInlineUSASkeleton } from 'components/Shared/MatchCardInlineUSA/__partials/Skeleton';
import { GameUsaSkeleton } from 'newcomponents/Shared/sportsbook/MarketsList/Skeleton';
import { MarketWarning } from 'newcomponents/Shared/sportsbook/MarketWarning';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSearchParams } from 'hooks/useSearchParams';
import { EBoostType } from 'store/reducers/sport-data';
import {
  getFavoriteActiveStateById,
  getFavoriteMarketGroupNames
} from 'store/selectors/fav-data';
import { getSportIdByAlias } from 'store/selectors/new-sportsbook';
import { getBoostedOdds } from 'store/selectors/sport-data';

const { BetBuilderContent } = lazy(
  () =>
    import(
      /* webpackChunkName: "BetBuilderContent" */ 'newcomponents/Shared/sportsbook/BetBuilderModal/components/BetBuilderContent'
    )
);

const { EventCollapse } = lazy(
  () =>
    import(
      /* webpackChunkName: "event-collapse-view" */ 'components/EventCollapse'
    )
);

const { EmptyFavorites } = lazy(
  () =>
    import(
      /* webpackChunkName: "empty-favorites-view" */ 'components/EmptyResultBoxes/EmptyFavorites'
    )
);

const { MatchCardInlineUSA } = lazy(
  () =>
    import(
      /* webpackChunkName: "events-item-view-usa" */ 'components/Shared/MatchCardInlineUSA'
    )
);

export const DEFAULT_SELECTED_MENU = 'all';

export interface Props {
  markets: Market[];
  game: Game;
  sportName: string;
  competitionName: string;
  isTwoColumn?: boolean;
  isTheatreMode?: boolean;
  isLive?: boolean;
  loading?: boolean;
  hideAll?: boolean;
  defaultGroupId?: string;
  currentSport?: string;
  regionAlias?: string;
  sport?: string;
  gameId?: number;
  compactView?: boolean;
  handleShowMoreClick?: () => void;
  targetId?: string;
  setTargetIdAsMarketColumnId?: boolean;
  competitionId?: number;
  isMultiview?: boolean;
  lazyScrollSettings?: {
    initialCount: number;
    loadMoreCount: number;
  };
  esport?: boolean;
}

const MARKETS_SEARCH_LETTERS_COUNT = 3;

export const MarketList = (props: Props): ReactElement => {
  const configs = useSportsbookConfigs();
  const { t } = useTranslation();
  const { game = props.gameId } = useSearchParams();

  const favoriteMarkets = useSelector(getFavoriteMarketGroupNames);
  const boostedOdds = useSelector(getBoostedOdds);
  const currentSportId = useSelector(
    getSportIdByAlias(
      (props.sport || props.currentSport) as string,
      configs.type
    )
  );

  const isMainMarketFavorited = useSelector(
    getFavoriteActiveStateById(MAIN_MARKETS_TYPE, 'markets')
  );

  const [filteredMarkets, setFilteredMarkets] = useState<Market[]>(
    props.markets
  );

  const [marketsFilterSearchValue, setMarketsFilterSearchValue] = useState<
    string | null
  >(null);

  const [menu, setMenu] = useState<string>(
    props.defaultGroupId || DEFAULT_SELECTED_MENU
  );

  const [isAllCollapsed, setIsAllCollapsed] = useState<boolean>();
  const [mainMarketsExpanded, setMainMarketsExpanded] = useState(true);

  const favGroups = getFavGroupsCount(props.markets);

  const isOutright = props.game.show_type === 'OUTRIGHT';
  const shouldShowMainMarkets =
    +configs.spbLayoutType === 1 ||
    (+configs.spbLayoutType === 1 &&
      props.game.show_type !== 'OUTRIGHT' &&
      (menu === DEFAULT_SELECTED_MENU ||
        (menu === ENotRealSports.favourites && isMainMarketFavorited)));

  const hasBoostedEvents =
    !!props.gameId &&
    boostedOdds?.[props.gameId] &&
    (Object.values(boostedOdds[props.gameId]).some(
      ({ type }) => type === EBoostType.ALL
    ) ||
      Object.values(boostedOdds[props.gameId]).some(
        ({ type }) =>
          type === (props.isLive ? EBoostType.LIVE : EBoostType.PREMATCH)
      ));

  const onChangeMenu = useCallback(
    (selectedMarket: string) => {
      let filteredArray: Market[] = [];

      if (!props.hideAll && selectedMarket === 'all') {
        filteredArray = props.markets;
      } else if (selectedMarket === ENotRealSports.favourites) {
        filteredArray = filterFavGroups(props.markets);
      } else if (selectedMarket === ENotRealSports.boostedOdds) {
        if (boostedOdds && props.gameId) {
          const boostedEvents = boostedOdds[props.gameId];

          filteredArray = props.markets.filter((market: Market) => {
            return Object.keys(market.event || {}).some(
              event => !!boostedEvents[event]
            );
          });
        }
      } else if (selectedMarket === ENotRealSports.betBuilder) {
        filteredArray = props.markets;
      } else {
        filteredArray = props.markets.filter(
          (market: Market) => (market.group_id || 'Other') === +selectedMarket
        );
      }

      setFilteredMarkets(filteredArray);
    },
    [props.markets, favoriteMarkets]
  );

  useEffect(() => {
    if (marketsFilterSearchValue !== null) {
      const selectedMenu = menu;
      setMenu(DEFAULT_SELECTED_MENU);

      return () => {
        setMenu(selectedMenu);
      };
    }
  }, [marketsFilterSearchValue !== null]);

  useEffect(() => {
    if (props.defaultGroupId && menu !== props.defaultGroupId) {
      setMenu(props.defaultGroupId);
    }
  }, [game, props.defaultGroupId]);

  useEffect(() => {
    if (
      menu !== props.defaultGroupId &&
      menu !== ENotRealSports.favourites &&
      menu !== ENotRealSports.boostedOdds &&
      menu !== ENotRealSports.betBuilder &&
      menu !== DEFAULT_SELECTED_MENU &&
      !props.markets.some(el => String(el.group_id) === menu)
    ) {
      if (props.defaultGroupId && props.defaultGroupId !== menu) {
        setMenu(props.defaultGroupId || DEFAULT_SELECTED_MENU);
      }

      return;
    }

    onChangeMenu(menu);
  }, [props.markets, onChangeMenu, menu, favoriteMarkets]);

  const groups = useMemo(() => {
    let groupsData: MarketGroup = {};

    const marketsToDisplay =
      marketsFilterSearchValue === null ||
      marketsFilterSearchValue.length < MARKETS_SEARCH_LETTERS_COUNT
        ? filteredMarkets
        : structuredClone(props.markets).filter(m => {
            const marketName = m.name
              .replace(/\b(\w*team 1\w*)\b/gi, props.game.team1_name)
              .replace(/\b(\w*team 2\w*)\b/gi, props.game.team2_name);

            return marketName
              .toLowerCase()
              .includes(marketsFilterSearchValue.toLowerCase().trim());
          });

    marketsToDisplay.forEach((market: Market) => {
      const key = `${market.type}-${market.name}`;

      if (key in groupsData) {
        groupsData[key].markets.push(market);
      } else {
        groupsData[key] = {
          name: market.name,
          id: market.group_id,
          key: key + market.order,
          type: key,
          colCount: market.col_count,
          markets: [market],
          expressId: market.prematch_express_id || market.express_id,
          cashout: Boolean(market.cashout),
          groupName: market.group_name,
          marketType: market.market_type || ''
        };
      }
    });

    if (SpringConfigs.USER_PERSONALIZATION_ENABLED && favoriteMarkets.length) {
      const orderedKeysArray: string[] = [];
      let groupsKeysArray = Object.keys(groupsData);
      favoriteMarkets.forEach(market => {
        if (groupsKeysArray.includes(market)) {
          orderedKeysArray.push(market);
        }
      });

      orderedKeysArray.length &&
        orderedKeysArray.forEach((item, index) => {
          groupsKeysArray = arrayMove(
            groupsKeysArray,
            groupsKeysArray.indexOf(item),
            index
          );
        });

      groupsData = groupsKeysArray.reduce((obj: MarketGroup, key) => {
        obj[key] = groupsData[key];

        return obj;
      }, {});
    }

    return groupsData;
  }, [filteredMarkets, favoriteMarkets, marketsFilterSearchValue]);

  const groupItems = useMemo(() => {
    let result;
    const objectValues = Object.values(groups);

    if (props.isTwoColumn) {
      const firstColumn = [];
      const secondColumn = [];

      for (let i = 0; i < objectValues.length; i++) {
        i % 2 === 0
          ? firstColumn.push(objectValues[i])
          : secondColumn.push(objectValues[i]);
      }

      result = [firstColumn, secondColumn];
    } else {
      result = [objectValues];
    }

    return result;
  }, [groups]);

  const marketGroups = useMemo(() => {
    return props.markets.reduce(
      (result, current) => ({
        ...result,
        [String(current.group_id)]: {
          name: current.group_name,
          isNew: current.is_new
        }
      }),
      {}
    );
  }, [props.markets]);

  const loadingCondition =
    !!filteredMarkets.length &&
    !groupItems[0].length &&
    marketsFilterSearchValue === null;

  const showEmptyPage =
    (!groupItems[0]?.length && menu !== ENotRealSports.favourites) ||
    (menu === ENotRealSports.favourites &&
      !isMainMarketFavorited &&
      !groupItems[0]?.length);

  const filteredGroups = useMemo(() => {
    if (
      !groupItems[0]?.length &&
      menu === ENotRealSports.favourites &&
      isMainMarketFavorited
    ) {
      return [MAIN_MARKETS_TYPE];
    }

    return groupItems;
  }, [groupItems, menu, isMainMarketFavorited]);

  return loadingCondition ? (
    <MarketsListSkeleton isLive={props.isLive} />
  ) : (
    <>
      <BetsMenu
        sportId={currentSportId}
        isAllCollapsed={isAllCollapsed}
        setIsAllCollapsed={setIsAllCollapsed}
        hasBoostedEvents={hasBoostedEvents}
        marketGroups={marketGroups}
        onChange={(selectedMarket: string) => setMenu(selectedMarket)}
        activeMenu={menu}
        favCount={favGroups}
        hideAll={props.hideAll}
        isLive={props.isLive}
        regionAlias={props.regionAlias}
        setSearchValue={setMarketsFilterSearchValue}
        searchValue={marketsFilterSearchValue}
        game={{
          team1_name: `${props.game.team1_name}`.toString(),
          team2_name: `${props.game.team2_name}`.toString(),
          sportcast_id: parseInt((props.game.sportcast_id || 0).toString()),
          id: props.game.id
        }}
      />

      {!props.isTwoColumn && !props.isLive && (
        <TextInfo textInfo={props.game.text_info} />
      )}
      {menu === ENotRealSports.betBuilder ? (
        <div className="marketsListBBIframeWrapper">
          <Suspense>
            <BetBuilderContent
              fixtureId={props.game.sportcast_id}
              sportId={currentSportId}
              showTitle={false}
              scale
            />
          </Suspense>
        </div>
      ) : (
        <MarketsWrapper
          style={{
            overflow: !isMobile() ? 'auto' : 'none',
            backgroundColor: props.esport ? 'var(--v3-black-2)' : 'transparent'
          }}
          isTheatreMode={props.isTheatreMode}
          data-popover-parent
          className="single-view-markets"
        >
          {(groupItems[0]?.length > 0 ||
            (menu === ENotRealSports.favourites && isMainMarketFavorited)) &&
            filteredGroups.map((groupItem, index) => (
              <MarketColumn
                id={
                  props.setTargetIdAsMarketColumnId && props.targetId
                    ? props.targetId
                    : ''
                }
                fullWidth={groupItems.length == 1}
                columnIndex={index}
                key={index}
                data-popover-parent
              >
                <RocketBet
                  key={`${props.gameId}-${props.game.team1_name}`}
                  game={props.game}
                  currentSport={props.currentSport}
                  gameId={props.gameId}
                  competitionName={props.competitionName}
                  pageType={configs.pageType}
                  isAllCollapsed={isAllCollapsed}
                />
                <LVision
                  key={`${props.gameId}-${props.game.team2_name}`}
                  game={props.game}
                  currentSport={props.currentSport}
                  gameId={props.gameId}
                  competitionName={props.competitionName}
                  competitionId={props.competitionId}
                  pageType={configs.pageType}
                  isAllCollapsed={isAllCollapsed}
                />
                {shouldShowMainMarkets && !isOutright && (
                  <Suspense fallback={<GameUsaSkeleton hideMoreWages />}>
                    <EventCollapse
                      onChange={setMainMarketsExpanded}
                      key={1}
                      title={
                        <>
                          <FavoriteButtonMarket isMainMarket />
                          <span className="main-markets-title">
                            {t('sportsbook.mainMarkets')}
                          </span>
                        </>
                      }
                      defaultActive={mainMarketsExpanded}
                    >
                      <Suspense
                        fallback={
                          <MatchCardInlineUSASkeleton
                            sportAlias={props.currentSport}
                            hideMoreWages
                          />
                        }
                      >
                        <MatchCardInlineUSA
                          game={props.game}
                          showGameTime={false}
                          includeRedirect={false}
                          sportAlias={props.currentSport}
                          competitionName={props.competitionName}
                        />
                      </Suspense>
                    </EventCollapse>
                  </Suspense>
                )}
                {props.compactView ? (
                  <MarketsGroupItemsNoLazy
                    isAllCollapsed={isAllCollapsed}
                    data={groupItem as MarketGroupItem[]}
                    menu={menu}
                    competitionName={props.competitionName}
                    sportName={props.sportName}
                    game={props.game}
                    handleShowMoreClick={props.handleShowMoreClick}
                  />
                ) : typeof groupItem !== 'string' ? (
                  <MarketsGroupItems
                    isAllCollapsed={isAllCollapsed}
                    sport={props.sport}
                    gameId={props.gameId}
                    data={groupItem}
                    menu={menu}
                    lazyScrollSettings={props.lazyScrollSettings}
                    competitionName={props.competitionName}
                    sportName={props.sportName}
                    game={props.game}
                    targetId={props.targetId}
                  />
                ) : null}
                {props.esport && <MarketWarning />}
              </MarketColumn>
            ))}

          {showEmptyPage &&
            !props.loading &&
            (props.isMultiview ? (
              <EmptyMarketsView />
            ) : (
              <div className="emptyFavorites__container">
                <Suspense fallback={<EmptyFavoritesSkeleton />}>
                  <EmptyFavorites
                    emptyText={t('sportsbook.emptyFavoriteMarkets')}
                  />
                </Suspense>
                {props.esport && <MarketWarning />}
              </div>
            ))}
        </MarketsWrapper>
      )}
    </>
  );
};
